import { useEffect, useRef, useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import {
  ArrowRight,
  AlertCircle,
  BarChart3,
  Bot,
  Building2,
  CheckCircle,
  Code2,
  Dumbbell,
  LayoutDashboard,
  Mail,
  Megaphone,
  Menu,
  MessageCircle,
  Phone,
  Pill,
  Plug,
  Rocket,
  Scissors,
  Search,
  Send,
  Shirt,
  ShoppingCart,
  Sparkles,
  Stethoscope,
  Users,
  Workflow,
  X,
} from 'lucide-react';
import logo from './assets/logo.png';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

const navItems = [
  { label: 'Services', href: '#services' },
  { label: 'Solutions', href: '#solutions' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Why Merchy', href: '#why-merchy' },
  { label: 'Contact', href: '#contact' },
];

const supportEmail = 'support@merchyapp.online';
const supportMailto = `mailto:${supportEmail}`;
const whatsappNumber = '01099196911';
const whatsappUrl =
  'https://wa.me/201099196911?text=Hi%20Merchy%2C%20I%27d%20like%20to%20discuss%20a%20process%20in%20my%20business%20that%20I%20want%20to%20improve%20or%20automate.';

const businessTypes = [
  'Real Estate',
  'E-commerce',
  'Clinic / Healthcare',
  'Gym / Fitness',
  'Atelier / Dress Rental',
  'Barbershop / Salon',
  'Pharmacy',
  'Marketing / Agency',
  'Professional Services',
  'Retail',
  'Other',
];

const initialFormState = {
  name: '',
  company: '',
  contact: '',
  businessType: '',
  otherBusinessType: '',
  process: '',
  preferredContact: 'WhatsApp',
};

const services = [
  {
    title: 'Custom Management Systems',
    eyebrow: 'Workflow software',
    description:
      'Lightweight systems built around the way your team already works, without the complexity of a full ERP.',
    icon: LayoutDashboard,
    cta: 'Request a Demo',
    examples: [
      'Reservations and booking management',
      'Customer and lead management',
      'Rental, item, and asset tracking',
      'Payments, deposits, and staff workflows',
      'Simple dashboards and sales pipelines',
    ],
    visual: 'calendar',
  },
  {
    title: 'Business Process Automation',
    eyebrow: 'Less admin work',
    description:
      'Connect tools, move data, create records, trigger notifications, and reduce repetitive back-office work.',
    icon: Workflow,
    cta: 'Automate a Process',
    examples: [
      'Moving data between systems',
      'Processing form submissions',
      'Scheduling follow-ups',
      'Generating recurring reports',
      'Processing spreadsheets and files',
    ],
    visual: 'workflow',
  },
  {
    title: 'WhatsApp Business Automation',
    eyebrow: 'Customer messages',
    description:
      'Automatically send the right message when bookings, payments, reservations, or workflow statuses change.',
    icon: MessageCircle,
    cta: 'Discuss a WhatsApp Workflow',
    examples: [
      'Booking confirmations',
      'Appointment reminders',
      'Payment reminders',
      'Status updates',
      'Internal staff alerts',
    ],
    visual: 'message',
  },
  {
    title: 'AI Assistants & Chatbots',
    eyebrow: 'Focused AI',
    description:
      'Practical assistants that answer questions, retrieve information, classify requests, and use approved tools.',
    icon: Bot,
    cta: 'Explore AI Automation',
    examples: [
      'Customer FAQ assistants',
      'Lead qualification assistants',
      'Internal business assistants',
      'Document and message extraction',
      'Meeting note summarization',
    ],
    visual: 'assistant',
  },
  {
    title: 'Lead & Sales Management',
    eyebrow: 'Pipeline clarity',
    description:
      'Turn scattered inquiries into an organized process with statuses, notes, reminders, and campaign reporting.',
    icon: Users,
    cta: 'See Lead Management Demo',
    examples: [
      'Lead capture and source tracking',
      'Sales pipeline stages',
      'Follow-up scheduling',
      'Meeting tracking',
      'Conversion reporting',
    ],
    visual: 'pipeline',
  },
  {
    title: 'Dashboards & Automated Reporting',
    eyebrow: 'Clear business data',
    description:
      'Collect and present the numbers your team prepares manually, so reports are useful without becoming a chore.',
    icon: BarChart3,
    cta: 'Automate My Reporting',
    examples: [
      'Management dashboards',
      'Sales and operations reports',
      'KPI tracking',
      'Campaign and lead reporting',
      'Custom business metrics',
    ],
    visual: 'chart',
  },
  {
    title: 'API & System Integration',
    eyebrow: 'Connected tools',
    description:
      'Connect compatible platforms so information moves between websites, forms, CRMs, databases, and dashboards.',
    icon: Plug,
    cta: 'Connect My Systems',
    examples: [
      'Website to management system',
      'Form to CRM',
      'CRM to WhatsApp',
      'Database to dashboard',
      'Business system to report',
    ],
    visual: 'integration',
  },
];

const industryUseCases = [
  {
    title: 'Real Estate',
    icon: Building2,
    manual: 'Capture incoming leads, assign them automatically, and make sure every opportunity gets followed up.',
    workflow: ['Lead', 'Capture', 'Assign', 'WhatsApp alert', 'Follow-up', 'Dashboard'],
    builds: [
      'Lead capture and assignment',
      'Customer requirements and property interest tracking',
      'Follow-up reminders and meeting tracking',
      'Campaign attribution and won/lost reporting',
    ],
    cta: 'Automate Your Real Estate Leads',
  },
  {
    title: 'E-commerce Operations',
    icon: ShoppingCart,
    manual: 'Keep orders, returns, customer messages, and operational updates moving without manual copying.',
    workflow: ['New order', 'Confirm', 'Notify team', 'Status update', 'Follow-up'],
    builds: [
      'Order and WhatsApp confirmation workflows',
      'Return and exchange request handling',
      'Customer service and lead capture automation',
      'Store, database, and reporting integrations',
    ],
    cta: 'Automate Your E-commerce Operations',
  },
  {
    title: 'Clinics',
    icon: Stethoscope,
    manual: 'Organize appointment administration, reminders, and reception workflows without touching clinical decisions.',
    workflow: ['Request', 'Slot selected', 'Stored', 'Confirmation', 'Reminder', 'Dashboard'],
    builds: [
      'Appointment booking and schedule management',
      'Patient records for administrative workflows',
      'Confirmation and reminder messages',
      'Daily appointment dashboards and staff notifications',
    ],
    cta: 'Automate Clinic Administration',
  },
  {
    title: 'Gyms & Fitness Businesses',
    icon: Dumbbell,
    manual: 'Track memberships, renewals, attendance, and class bookings from one organized workflow.',
    workflow: ['Signup', 'Record membership', 'Track expiry', 'Reminder', 'Renewal', 'Dashboard'],
    builds: [
      'Membership management and expiration alerts',
      'Payment and attendance tracking',
      'Class, session, and personal training bookings',
      'Renewal follow-ups and membership dashboards',
    ],
    cta: 'Automate Your Gym Operations',
  },
  {
    title: 'Ateliers & Dress Rental',
    icon: Shirt,
    manual: 'Manage dress availability, reservations, deposits, pickups, returns, and cleaning status clearly.',
    workflow: ['Select dress', 'Check availability', 'Reserve', 'Deposit', 'Pickup', 'Return', 'Cleaning'],
    builds: [
      'Dress inventory and availability calendars',
      'Reservations, deposits, payments, and customer profiles',
      'Pickup, return, cleaning, and damage tracking',
      'WhatsApp reminders, revenue dashboards, and most-rented item reports',
    ],
    cta: 'Build My Rental Management System',
  },
  {
    title: 'Barbershops & Salons',
    icon: Scissors,
    manual: 'Keep bookings, staff schedules, reminders, and customer history organized automatically.',
    workflow: ['Booking', 'Reserve slot', 'Confirm', 'Reminder', 'Service', 'History'],
    builds: [
      'Online booking and staff calendars',
      'Appointment confirmations and WhatsApp reminders',
      'Customer profiles, visit history, and no-show tracking',
      'Service tracking, loyalty reminders, and daily schedule dashboards',
    ],
    cta: 'Automate My Booking Workflow',
  },
  {
    title: 'Pharmacies',
    icon: Pill,
    manual: 'Improve operational visibility with stock alerts, supplier tracking, branch reports, and dashboards.',
    workflow: ['Low stock', 'Alert', 'Reorder list', 'Supplier task', 'Report'],
    builds: [
      'Stock tracking and reorder alerts',
      'Supplier tracking and staff notifications',
      'Sales, branch, and management dashboards',
      'Expiry-date tracking where appropriate',
    ],
    cta: 'Improve Pharmacy Operations',
  },
  {
    title: 'Marketing & Agencies',
    icon: Megaphone,
    manual: 'Connect lead capture, campaign attribution, follow-ups, reports, and client operations.',
    workflow: ['Lead form', 'CRM', 'Sales alert', 'Follow-up task', 'Meeting', 'Report'],
    builds: [
      'Lead capture, CRM automation, and campaign attribution',
      'Follow-up reminders, proposal tracking, and client onboarding',
      'Automated reporting and data consolidation',
      'Meeting notes, AI-assisted summaries, and performance dashboards',
    ],
    cta: 'Automate Agency Operations',
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Tell Us Your Process',
    description: 'Show us the workflow that takes too much time, creates mistakes, or is difficult to manage.',
    icon: MessageCircle,
  },
  {
    number: '02',
    title: 'We Analyze & Plan',
    description: 'We understand who is involved, what tools are used, and where automation or software will help.',
    icon: Search,
  },
  {
    number: '03',
    title: 'We Build & Integrate',
    description: 'We develop the system, connect compatible tools, and test it with real workflow scenarios.',
    icon: Code2,
  },
  {
    number: '04',
    title: 'Launch & Support',
    description: 'We deploy the solution and provide support as your team starts using it.',
    icon: Rocket,
  },
];

const workflowRows = [
  ['Lead', 'CRM', 'Follow-up', 'WhatsApp'],
  ['Booking', 'System', 'Reminder', 'Staff'],
  ['Business Data', 'Automation', 'Dashboard', 'Report'],
];

const integrationItems = ['Website', 'Form', 'CRM', 'WhatsApp', 'Database', 'Dashboard', 'Google Sheets', 'Report'];
const isProduction = process.env.NODE_ENV === 'production';

function ProductionAnalytics() {
  if (!isProduction) return null;

  const { Analytics } = require('@vercel/analytics/react');
  return <Analytics mode="production" />;
}

function Logo({ className = '' }) {
  return <img src={logo} alt="Merchy" className={`h-8 w-auto origin-left scale-[4] ${className}`} />;
}

function SectionHeading({ eyebrow, title, children, centered = false }) {
  return (
    <div className={`reveal ${centered ? 'mx-auto text-center' : ''} max-w-3xl`}>
      {eyebrow ? (
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-accent/90">{eyebrow}</p>
      ) : null}
      <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">{title}</h2>
      {children ? <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">{children}</p> : null}
    </div>
  );
}

function FeatureRows({ items }) {
  return (
    <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {items.slice(0, 4).map((item) => (
        <div
          key={item}
          className="group flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2.5 text-sm text-gray-300 transition hover:border-accent/25 hover:bg-white/[0.04]"
        >
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-emerald-400/10 text-emerald-300 transition group-hover:bg-emerald-400/15">
            <CheckCircle aria-hidden="true" className="h-3.5 w-3.5" />
          </span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}

function ErrorText({ children }) {
  if (!children) return null;

  return (
    <p className="mt-2 flex items-center gap-2 text-xs text-rose-300">
      <AlertCircle aria-hidden="true" className="h-3.5 w-3.5" />
      {children}
    </p>
  );
}

function WorkflowPreview() {
  return (
    <div className="rounded-[28px] border border-white/10 bg-card/85 p-4 shadow-card backdrop-blur">
      <div className="rounded-2xl border border-white/10 bg-secondary/90 p-4">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent/85">Workflow Map</p>
            <h3 className="mt-1 text-lg font-semibold text-white">Manual process to digital flow</h3>
          </div>
          <div className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-200">
            Live
          </div>
        </div>
        <div className="space-y-4">
          {workflowRows.map((row) => (
            <div key={row.join('-')} className="grid grid-cols-1 gap-2 sm:grid-cols-4">
              {row.map((item, index) => (
                <div key={item} className="relative">
                  <div className="rounded-xl border border-white/10 bg-white/[0.035] px-3 py-3 text-sm font-medium text-white">
                    {item}
                  </div>
                  {index < row.length - 1 ? (
                    <ArrowRight
                      aria-hidden="true"
                      className="absolute -right-4 top-1/2 z-10 hidden h-4 w-4 -translate-y-1/2 text-accent sm:block"
                    />
                  ) : null}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3">
          {[
            ['62%', 'less manual entry'],
            ['12', 'steps simplified'],
            ['4', 'systems connected'],
          ].map(([value, label]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-black/25 p-3">
              <p className="text-2xl font-semibold text-white">{value}</p>
              <p className="mt-1 text-xs text-muted">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MiniVisual({ type }) {
  if (type === 'calendar') {
    return (
      <div className="grid grid-cols-4 gap-2">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Next'].map((day, index) => (
          <div
            key={day}
            className={`rounded-lg border px-2 py-3 text-center text-xs ${
              index === 2 || index === 5
                ? 'border-accent/40 bg-accent/15 text-white'
                : 'border-white/10 bg-white/[0.035] text-muted'
            }`}
          >
            {day}
          </div>
        ))}
      </div>
    );
  }

  if (type === 'workflow' || type === 'integration') {
    const items = type === 'integration' ? ['Form', 'CRM', 'WhatsApp', 'Report'] : ['Form', 'Record', 'Notify', 'Follow-up'];
    return (
      <div className="flex flex-wrap items-center gap-2">
        {items.map((item, index) => (
          <div key={item} className="flex items-center gap-2">
            <span className="rounded-lg border border-white/10 bg-white/[0.035] px-3 py-2 text-xs text-white">
              {item}
            </span>
            {index < items.length - 1 ? <ArrowRight aria-hidden="true" className="h-4 w-4 text-accent" /> : null}
          </div>
        ))}
      </div>
    );
  }

  if (type === 'message') {
    return (
      <div className="space-y-2">
        <div className="ml-auto max-w-[88%] rounded-2xl rounded-br-md bg-emerald-500/15 px-4 py-3 text-xs text-emerald-50">
          Your booking is confirmed for Thursday at 6:00 PM.
        </div>
        <div className="max-w-[78%] rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.04] px-4 py-3 text-xs text-white">
          Reminder scheduled automatically.
        </div>
      </div>
    );
  }

  if (type === 'assistant') {
    return (
      <div className="rounded-xl border border-white/10 bg-black/25 p-3">
        <div className="mb-3 flex items-center gap-2">
          <Bot aria-hidden="true" className="h-5 w-5 text-accent" />
          <span className="text-xs font-semibold text-white">Business assistant</span>
        </div>
        <div className="h-2 w-11/12 rounded-full bg-white/15" />
        <div className="mt-2 h-2 w-8/12 rounded-full bg-white/10" />
        <div className="mt-3 rounded-lg bg-accent/15 px-3 py-2 text-xs text-accent">Action ready: update CRM</div>
      </div>
    );
  }

  if (type === 'pipeline') {
    return (
      <div className="grid grid-cols-3 gap-2">
        {['New', 'Meeting', 'Won'].map((stage, index) => (
          <div key={stage} className="rounded-lg border border-white/10 bg-white/[0.035] p-2">
            <p className="text-xs text-muted">{stage}</p>
            <div className={`mt-2 h-12 rounded-md ${index === 2 ? 'bg-emerald-400/20' : 'bg-accent/15'}`} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-24 items-end gap-2">
      {[42, 68, 52, 86, 74].map((height, index) => (
        <div
          key={height}
          className="flex-1 rounded-t-lg bg-gradient-to-t from-accent/35 to-glow/35"
          style={{ height: `${height}%` }}
        >
          <span className="sr-only">Chart bar {index + 1}</span>
        </div>
      ))}
    </div>
  );
}

function HomePage() {
  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});

  const resolvedBusinessType =
    formData.businessType === 'Other' ? formData.otherBusinessType.trim() : formData.businessType;

  const updateFormField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) nextErrors.name = 'Please enter your name.';
    if (!formData.contact.trim()) nextErrors.contact = 'Please add an email or phone number.';
    if (!formData.businessType) nextErrors.businessType = 'Please choose a business type.';
    if (formData.businessType === 'Other' && !formData.otherBusinessType.trim()) {
      nextErrors.otherBusinessType = 'Please tell us your business type.';
    }
    if (!formData.process.trim()) nextErrors.process = 'Please describe the process you want to improve.';

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildInquiryMessage = () =>
    [
      "Hi Merchy, I'd like to discuss a business process.",
      '',
      `Name: ${formData.name.trim()}`,
      `Business: ${formData.company.trim() || 'Not provided'}`,
      `Business Type: ${resolvedBusinessType}`,
      `Email / Phone: ${formData.contact.trim()}`,
      '',
      'Process I want to improve:',
      formData.process.trim(),
      '',
      `Preferred contact method: ${formData.preferredContact}`,
    ].join('\n');

  const handleProposalSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const message = buildInquiryMessage();

    if (formData.preferredContact === 'Email') {
      const subject = `Merchy Project Inquiry - ${formData.company.trim() || resolvedBusinessType || formData.name.trim()}`;
      window.open(`${supportMailto}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`, '_self');
      return;
    }

    window.open(`https://wa.me/201099196911?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <main id="top" className="bg-grid overflow-hidden">
      <section className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-20 pt-20 lg:grid-cols-[1.02fr_0.98fr] lg:items-center lg:px-10 lg:pt-24">
        <div className="hero-glow left-[-10rem] top-[-8rem]" />
        <div className="reveal">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-4 py-2 text-sm text-muted">
            <Sparkles aria-hidden="true" className="h-4 w-4 text-accent" />
            Software, automation, and AI for real business workflows
          </div>
          <h1 className="text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
            Software That Makes Your Business Easier to Run
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted">
            We build simple management systems, business automations, AI assistants, and integrations around the way
            your business actually works.
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-gray-400">
            From managing reservations and leads to automating reminders, reports, and repetitive tasks, we turn manual
            processes into simple digital workflows.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <a href="#contact" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full border border-accent/50 bg-accent px-7 py-3 text-sm font-semibold text-white shadow-glow">
              Tell Us What You Want to Automate
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </a>
            <a href="#solutions" className="inline-flex items-center justify-center rounded-full border border-border bg-card px-7 py-3 text-sm font-semibold text-white transition hover:border-accent/40">
              Explore Our Solutions
            </a>
          </div>
        </div>
        <div className="reveal relative">
          <div className="absolute -inset-12 -z-10 rounded-full bg-[radial-gradient(circle,rgba(91,140,255,0.22),transparent_65%)] blur-3xl" />
          <WorkflowPreview />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="reveal grid grid-cols-1 gap-4 rounded-[28px] border border-white/10 bg-card/70 p-4 shadow-card sm:grid-cols-3">
          {[
            ['Management systems', 'Built around your team'],
            ['Automation workflows', 'Less repeated admin'],
            ['AI where useful', 'Practical, focused assistants'],
          ].map(([title, copy]) => (
            <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.025] p-5">
              <CheckCircle aria-hidden="true" className="h-5 w-5 text-emerald-300" />
              <h2 className="mt-4 text-lg font-semibold text-white">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="services" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <SectionHeading eyebrow="What We Build" title="Useful software for everyday business operations" centered>
          You do not need to know what technology you need. Show us the repetitive process and we will determine what
          can be simplified, connected, or automated.
        </SectionHeading>
        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {services.map(({ title, eyebrow, description, icon: Icon, cta, examples, visual }) => (
            <article key={title} className="reveal glow-hover rounded-2xl border border-white/10 bg-card/80 p-5 shadow-card">
              <div className="flex items-start gap-4">
                <div className="rounded-xl border border-accent/20 bg-accent/10 p-3">
                  <Icon aria-hidden="true" className="h-6 w-6 text-accent" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent/85">{eyebrow}</p>
                  <h3 className="mt-2 text-xl font-semibold text-white">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{description}</p>
                </div>
              </div>
              <div className="mt-5 rounded-2xl border border-white/10 bg-secondary/80 p-4">
                <MiniVisual type={visual} />
              </div>
              <FeatureRows items={examples} />
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <a href="#contact" className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:text-white">
                  {cta}
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </a>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 transition hover:text-white"
                >
                  <MessageCircle aria-hidden="true" className="h-4 w-4" />
                  Chat on WhatsApp
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="solutions" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="rounded-[32px] border border-white/10 bg-[linear-gradient(135deg,rgba(14,14,20,0.96),rgba(5,5,8,0.96))] p-6 shadow-card sm:p-8 lg:p-10">
          <SectionHeading eyebrow="See What Merchy Can Automate" title="Manual workflows we can turn into simple systems" centered>
            Every business works differently. Here are examples of scattered processes we can organize, connect, and
            automate without asking you to define the technology first.
          </SectionHeading>
          <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {industryUseCases.map(({ title, manual, workflow, builds, cta, icon: Icon }) => (
              <article key={title} className="reveal rounded-2xl border border-white/10 bg-white/[0.035] p-5 transition hover:-translate-y-1 hover:border-accent/35">
                <div className="flex items-start gap-4">
                  <div className="rounded-xl border border-accent/20 bg-accent/10 p-3">
                    <Icon aria-hidden="true" className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted">{manual}</p>
                  </div>
                </div>
                <div className="mt-5 rounded-2xl border border-white/10 bg-black/25 p-4">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-accent/85">Improved workflow</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {workflow.map((step, index) => (
                      <div key={`${title}-${step}`} className="flex items-center gap-2">
                        <span className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-medium text-white">
                          {step}
                        </span>
                        {index < workflow.length - 1 ? <ArrowRight aria-hidden="true" className="h-4 w-4 text-accent" /> : null}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-5">
                  <p className="text-sm font-semibold text-white">What Merchy can build</p>
                  <FeatureRows items={builds} />
                </div>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <a href="#contact" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full border border-accent/50 bg-accent px-5 py-3 text-sm font-semibold text-white shadow-glow">
                    {cta}
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </a>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-emerald-100 transition hover:border-emerald-300/50"
                  >
                    <MessageCircle aria-hidden="true" className="h-4 w-4" />
                    Chat on WhatsApp
                  </a>
                </div>
              </article>
            ))}
          </div>
          <div className="reveal mt-8 flex justify-center">
            <a href="#contact" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full border border-accent/50 bg-accent px-7 py-3 text-sm font-semibold text-white shadow-glow">
              Tell Us How Your Business Works
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <SectionHeading eyebrow="How It Works" title="A clear path from manual process to working system" centered>
          We focus on understanding the process first, then choosing the right technology for the job.
        </SectionHeading>
        <div className="relative mt-12">
          <div className="absolute left-6 top-6 hidden h-[calc(100%-3rem)] w-px bg-gradient-to-b from-accent via-white/15 to-transparent lg:block" />
          <div className="grid grid-cols-1 gap-4">
            {processSteps.map((step) => {
              const StepIcon = step.icon;
              return (
              <article key={step.number} className="reveal grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-card/80 p-5 shadow-card lg:grid-cols-[84px_1fr] lg:items-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-accent/35 bg-accent/10 text-accent">
                  <StepIcon aria-hidden="true" className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent/80">{step.number}</p>
                  <h3 className="mt-1 text-xl font-semibold text-white">{step.title}</h3>
                  <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted">{step.description}</p>
                </div>
              </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="reveal relative overflow-hidden rounded-[32px] border border-accent/20 bg-[linear-gradient(135deg,rgba(124,92,255,0.2),rgba(10,10,18,0.96)_45%,rgba(91,140,255,0.14))] p-8 shadow-[0_24px_90px_rgba(124,92,255,0.18)] lg:p-12">
          <div className="absolute right-0 top-0 h-48 w-48 rounded-full bg-accent/20 blur-3xl" />
          <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-[1fr_0.85fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-accent/90">Not Sure What You Need?</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Start with: "This is how we currently do it."
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
                You do not need to ask for an AI agent, API, CRM, database, or automation. Tell us the process, and we
                will help identify what can be simplified.
              </p>
            </div>
            <a href="#contact" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full border border-accent/50 bg-accent px-7 py-3 text-sm font-semibold text-white shadow-glow lg:justify-self-end">
              Tell Us Your Process
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section id="why-merchy" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <SectionHeading eyebrow="Built Around Your Business" title="Solve the process first. Choose the technology second.">
            We do not believe every business needs complicated enterprise software. Sometimes the right solution is a
            small management system, an automated workflow, an AI assistant, or simply connecting two systems.
          </SectionHeading>
          <div className="reveal grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              ['Simple when possible', 'We avoid overbuilding when a focused workflow solves the problem.'],
              ['AI with purpose', 'We use AI where it adds value, not where a simple automation works better.'],
              ['Operational detail', 'We care about statuses, handoffs, reminders, reports, and daily team behavior.'],
              ['Support after launch', 'Systems can evolve as your process becomes clearer in real use.'],
            ].map(([title, copy]) => (
              <article key={title} className="rounded-2xl border border-white/10 bg-card/80 p-5">
                <h3 className="text-lg font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="reveal rounded-[32px] border border-white/10 bg-card/80 p-6 shadow-card lg:p-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-accent/90">Integration examples</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">Connect the tools your team already uses</h2>
              <p className="mt-4 text-base leading-relaxed text-muted">
                Compatible systems can pass information automatically so your team spends less time copying, checking,
                and formatting the same data.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {integrationItems.map((item) => (
                <div key={item} className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 text-center text-sm font-medium text-white">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="reveal relative overflow-hidden rounded-[36px] border border-accent/25 bg-[linear-gradient(135deg,rgba(15,15,15,0.98),rgba(25,16,48,0.96))] px-6 py-14 text-center shadow-[0_24px_90px_rgba(124,92,255,0.18)] sm:px-10">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/70 to-transparent" />
          <h2 className="mx-auto max-w-3xl text-3xl font-semibold tracking-tight text-white sm:text-5xl">
            Have a Process You Want to Improve?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
            Tell us what your team currently does manually and what you wish worked better. We will explore whether
            software or automation can solve it.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a href="#contact" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full border border-accent/50 bg-accent px-7 py-3 text-sm font-semibold text-white shadow-glow">
              Discuss Your Idea
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </a>
            <a href={whatsappUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-7 py-3 text-sm font-semibold text-emerald-100 transition hover:border-emerald-300/50">
              <MessageCircle aria-hidden="true" className="h-4 w-4" />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section id="contact" className="relative mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="absolute inset-x-0 top-8 -z-10 h-52 bg-[radial-gradient(circle,rgba(124,92,255,0.22),transparent_70%)] blur-3xl" />
        <div className="grid grid-cols-1 gap-8 rounded-[32px] border border-white/10 bg-card/85 p-6 shadow-card backdrop-blur lg:grid-cols-[0.8fr_1.2fr] lg:p-10">
          <div className="reveal">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-accent/90">Contact</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Tell Us What Your Team Still Does Manually
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted">
              Explain how your business currently handles the process. We'll help identify what can be simplified,
              automated, or turned into software.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a href="#contact-form" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full border border-accent/50 bg-accent px-6 py-3 text-sm font-semibold text-white shadow-glow">
                Discuss Your Idea
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-6 py-3 text-sm font-semibold text-emerald-100 transition hover:border-emerald-300/50"
              >
                <MessageCircle aria-hidden="true" className="h-4 w-4" />
                Chat on WhatsApp
              </a>
            </div>
            <div className="mt-6 space-y-3 text-sm text-gray-300">
              <a href={supportMailto} className="flex items-center gap-3 transition hover:text-white">
                <Mail aria-hidden="true" className="h-4 w-4 text-accent" />
                {supportEmail}
              </a>
              <a href={whatsappUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 transition hover:text-white">
                <Phone aria-hidden="true" className="h-4 w-4 text-accent" />
                WhatsApp: {whatsappNumber}
              </a>
            </div>
          </div>
          <form id="contact-form" className="reveal grid grid-cols-1 gap-4" onSubmit={handleProposalSubmit} noValidate>
            <label className="block">
              <span className="text-sm font-medium text-white">Name</span>
              <input
                name="name"
                value={formData.name}
                onChange={(event) => updateFormField('name', event.target.value)}
                placeholder="Your name"
                className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none transition placeholder:text-gray-500 focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
              <ErrorText>{formErrors.name}</ErrorText>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-white">Business / Company</span>
              <input
                name="company"
                value={formData.company}
                onChange={(event) => updateFormField('company', event.target.value)}
                placeholder="Business name"
                className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none transition placeholder:text-gray-500 focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-white">Email or Phone</span>
              <input
                name="contact"
                value={formData.contact}
                onChange={(event) => updateFormField('contact', event.target.value)}
                placeholder="How can we reach you?"
                className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none transition placeholder:text-gray-500 focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
              <ErrorText>{formErrors.contact}</ErrorText>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-white">Business Type</span>
              <select
                name="businessType"
                value={formData.businessType}
                onChange={(event) => updateFormField('businessType', event.target.value)}
                className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
              >
                <option value="" disabled>Select your business type</option>
                {businessTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <ErrorText>{formErrors.businessType}</ErrorText>
            </label>
            {formData.businessType === 'Other' ? (
              <label className="block">
                <span className="text-sm font-medium text-white">Tell us your business type</span>
                <input
                  name="otherBusinessType"
                  value={formData.otherBusinessType}
                  onChange={(event) => updateFormField('otherBusinessType', event.target.value)}
                  placeholder="Example: Logistics, education, manufacturing..."
                  className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none transition placeholder:text-gray-500 focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
                <ErrorText>{formErrors.otherBusinessType}</ErrorText>
              </label>
            ) : null}
            <label className="block">
              <span className="text-sm font-medium text-white">What process would you like to improve?</span>
              <textarea
                name="process"
                value={formData.process}
                onChange={(event) => updateFormField('process', event.target.value)}
                rows="5"
                placeholder="Example: We receive leads from Instagram and WhatsApp, manually copy them into Excel, and sometimes forget to follow up..."
                className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white outline-none transition placeholder:text-gray-500 focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
              <ErrorText>{formErrors.process}</ErrorText>
            </label>
            <fieldset>
              <legend className="text-sm font-medium text-white">Preferred contact method</legend>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  ['WhatsApp', MessageCircle],
                  ['Email', Mail],
                  ['Phone', Phone],
                ].map(([method, MethodIcon]) => {
                  const isSelected = formData.preferredContact === method;

                  return (
                    <button
                      key={method}
                      type="button"
                      onClick={() => updateFormField('preferredContact', method)}
                      className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition ${
                        isSelected
                          ? 'border-accent/55 bg-accent/15 text-white'
                          : 'border-white/10 bg-black/20 text-muted hover:border-accent/30 hover:text-white'
                      }`}
                    >
                      <MethodIcon aria-hidden="true" className="h-4 w-4" />
                      <span className="hidden sm:inline">{method}</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <button type="submit" className="btn-glow inline-flex items-center justify-center gap-2 rounded-full border border-accent/50 bg-accent px-7 py-3 text-sm font-semibold text-white shadow-glow">
              Discuss My Process
              <Send aria-hidden="true" className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.12 }
    );

    const targets = document.querySelectorAll('.reveal');
    targets.forEach((target) => observer.observe(target));

    return () => {
      targets.forEach((target) => observer.unobserve(target));
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className="bg-base text-white">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-base/78 backdrop-blur-xl">
        <nav className="relative mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10" ref={menuRef}>
          <a href="#top" className="flex items-center gap-3" aria-label="Merchy home">
            <Logo />
          </a>
          <div className="hidden items-center gap-7 text-sm text-muted md:flex">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} className="transition hover:text-white">
                {item.label}
              </a>
            ))}
          </div>
          <div className="hidden items-center gap-3 md:flex">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-100 transition hover:border-emerald-300/50 hover:text-white"
            >
              <MessageCircle aria-hidden="true" className="h-4 w-4" />
              WhatsApp
            </a>
            <a href="#contact" className="btn-glow inline-flex items-center justify-center rounded-full border border-accent/50 bg-accent px-5 py-2 text-sm font-semibold text-white shadow-glow">
              Tell Us What You Want to Automate
            </a>
          </div>
          <button
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="rounded-lg border border-border bg-card p-2 text-white transition hover:border-accent/40 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 md:hidden"
          >
            {isMenuOpen ? <X aria-hidden="true" className="h-5 w-5" /> : <Menu aria-hidden="true" className="h-5 w-5" />}
          </button>
          {isMenuOpen ? (
            <div className="absolute right-4 top-16 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-white/10 bg-black/95 p-4 text-sm text-white shadow-lg backdrop-blur md:hidden">
              <div className="flex flex-col gap-3">
                {navItems.map((item) => (
                  <a key={item.href} href={item.href} className="rounded-xl px-3 py-2 transition hover:bg-white/5" onClick={closeMenu}>
                    {item.label}
                  </a>
                ))}
                <a href="#contact" className="btn-glow mt-2 rounded-full border border-accent/50 bg-accent px-4 py-3 text-center text-sm font-semibold text-white shadow-glow" onClick={closeMenu}>
                  Tell Us What You Want to Automate
                </a>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-3 text-center text-sm font-semibold text-emerald-100"
                  onClick={closeMenu}
                >
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          ) : null}
        </nav>
      </header>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        aria-label="Chat with Merchy on WhatsApp"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-[0_18px_44px_rgba(16,185,129,0.28)] transition hover:-translate-y-1 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300/60 sm:px-5"
      >
        <MessageCircle aria-hidden="true" className="h-5 w-5" />
        <span className="hidden sm:inline">Chat on WhatsApp</span>
        <span className="sm:hidden">WhatsApp</span>
      </a>

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
      </Routes>

      <footer className="border-t border-border bg-base">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-12 md:grid-cols-[1.3fr_1fr] lg:px-10">
          <div>
            <Logo />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-muted">
              Software, automation, and AI built around the way your business works.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-white">Navigate</p>
              <ul className="mt-4 space-y-2 text-sm text-muted">
                {navItems.slice(0, 4).map((item) => (
                  <li key={item.href}>
                    <a href={item.href} className="transition hover:text-white">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium text-white">Contact</p>
              <ul className="mt-4 space-y-2 text-sm text-muted">
                <li>
                  <a href="#contact" className="transition hover:text-white">Contact Form</a>
                </li>
                <li>
                  <a href={supportMailto} className="transition hover:text-white">{supportEmail}</a>
                </li>
                <li>
                  <a href={whatsappUrl} target="_blank" rel="noreferrer" className="transition hover:text-white">WhatsApp {whatsappNumber}</a>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium text-white">Legal</p>
              <ul className="mt-4 space-y-2 text-sm text-muted">
                <li>
                  <Link to="/privacy" className="transition hover:text-white">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/terms" className="transition hover:text-white">Terms of Service</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
      <ProductionAnalytics />
    </div>
  );
}

export default App;
